import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faLinkedin, faYoutube, faWhatsapp, faFacebook } from "@fortawesome/free-brands-svg-icons";
import { useSiteData } from "@/context/SiteDataContext";

const Footer = () => {
  const location = useLocation();
  const settings = useSiteData();

  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  return (
    <footer className="bg-background border-t border-white/5 py-12">
      <div className="container">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-2">
            <Link to="/" className="block mb-6 relative h-12 w-48">
              <img 
                src="/logo/Logo1.png" 
                alt="VCapz Media Logo" 
                className="w-full h-full object-contain object-left"
              />
            </Link>
            <p className="text-text-muted max-w-sm mb-8">
              Your partner in digital growth through premium content and social media management. 
              Elevating brands through creativity and strategy.
            </p>
            
            {/* Social Icons using Font Awesome */}
            <div className="flex gap-4 mb-8">
              {settings?.instagram && (
                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-xl text-text-muted hover:text-pink-500 hover:bg-pink-500/10 transition-all border border-white/5 hover:border-pink-500/20" title="Instagram">
                  <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                </a>
              )}
              {settings?.linkedin && (
                <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-xl text-text-muted hover:text-blue-500 hover:bg-blue-500/10 transition-all border border-white/5 hover:border-blue-500/20" title="LinkedIn">
                  <FontAwesomeIcon icon={faLinkedin} className="text-xl" />
                </a>
              )}
              {settings?.youtube && (
                <a href={settings.youtube} target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-xl text-text-muted hover:text-red-500 hover:bg-red-500/10 transition-all border border-white/5 hover:border-red-500/20" title="YouTube">
                  <FontAwesomeIcon icon={faYoutube} className="text-xl" />
                </a>
              )}
              {settings?.facebook && (
                <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-xl text-text-muted hover:text-blue-500 hover:bg-blue-500/10 transition-all border border-white/5 hover:border-blue-500/20" title="Facebook">
                  <FontAwesomeIcon icon={faFacebook} className="text-xl" />
                </a>
              )}
              {settings?.whatsapp && (
                <a href={settings.whatsapp.startsWith("http") ? settings.whatsapp : `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-xl text-text-muted hover:text-green-500 hover:bg-green-500/10 transition-all border border-white/5 hover:border-green-500/20" title="WhatsApp">
                  <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
                </a>
              )}
            </div>

            {/* Dynamic Contact Pills */}
            <div className="flex flex-wrap gap-4 text-xs font-semibold uppercase tracking-widest text-text-muted">
              {settings?.email && (
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/5">
                  <Mail size={12} className="text-primary" /> {settings.email}
                </div>
              )}
              {settings?.phone && (
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/5">
                  <Phone size={12} className="text-primary" /> {settings.phone}
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-sm">Quick Links</h4>
            <ul className="space-y-4">
              <li><Link to="/" className="text-text-muted hover:text-primary transition-colors">Home</Link></li>
              <li><a href="/#services" className="text-text-muted hover:text-primary transition-colors">Services</a></li>
              <li><a href="/#work" className="text-text-muted hover:text-primary transition-colors">Portfolio</a></li>
              <li><Link to="/blog" className="text-text-muted hover:text-primary transition-colors">Blog</Link></li>
              <li><a href="/#branches" className="text-text-muted hover:text-primary transition-colors">Branches</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-sm">Corporate</h4>
            <ul className="space-y-4">
              <li><Link to="/privacy" className="text-text-muted hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-text-muted hover:text-primary transition-colors">Terms of Service</Link></li>
              {settings?.address && (
                <li className="flex gap-2 items-start text-text-muted mt-6">
                  <MapPin size={16} className="shrink-0 text-primary" />
                  <span className="text-xs leading-relaxed">{settings.address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-text-muted">
            © {new Date().getFullYear()} VCapz Media. All rights reserved.
          </p>
          <p className="text-sm text-text-muted">
            Elevating Brands Globally.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
